import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { BookmarkTask, WorkspaceAssignmentTask } from '@/types/tasks';
import { createAppAsyncThunk } from '../withTypes';
import { Status, STATUSES } from '@/types/store';
import { RootState } from '..';
import {
  TaskPeerReviewData,
  TaskWithData,
  UpdateTaskPeerReviewMutation,
  UpdateTaskPeerReviewMutationVariables,
  UpsertTaskNoteMutation,
  UpsertTaskNoteMutationVariables,
} from '@/graphql/types';
import {
  BOOKMARK_TASK_MUTATION,
  FETCH_PEER_REVIEW_TASKS,
  UPDATE_TASK_PEER_REVIEW,
  UPSERT_TASK_NOTE,
} from '@/graphql/queries/tasks';
import { graphqlMutation, graphqlRequest } from '@/api/graphqlClient';
import { getNotes, showToast } from '@/utils/utils';
import { errorMessages } from '@/utils/errorMessages';
import i18n from '@i18n/i18n.config';

export interface PeerReview {
  allTaskList: WorkspaceAssignmentTask[];
  sourceTranslation: string;
  selectedTaskId?: number | null;
  status: Status;

}

interface BookmarkPeerReviewTask {
  taskId: number;
  isBookmarked: boolean;
}

const initialState: PeerReview = {
  sourceTranslation: '',
  allTaskList: [],
  selectedTaskId: 0,
  status: STATUSES.IDLE,
};

interface UpsertNote {
  taskId: number;
  userId: number;
  noteText: string;
  noteId?: number | null;
}

export const fetchPeerReviewTaskList = createAppAsyncThunk(
  'assignments/fetchPeerReviewTaskList',
  async (assignmentId: number) => {
    const data = await graphqlRequest<{ assignmentId: number }, { tasksPerAssignment: TaskWithData[] }>(
      FETCH_PEER_REVIEW_TASKS,
      { assignmentId },
      'no-cache',
    );

    const filteredData = data.tasksPerAssignment.filter(
      (task: TaskWithData) => task.data?.__typename === 'TaskPeerReviewData',
    );

    if (filteredData.length === 0) {
      throw new Error(errorMessages.taskNotFound);
    }

    return filteredData.map((task: TaskWithData) => {
      const taskData = task.data as TaskPeerReviewData;

      return {
        id: task.id,
        title: `${i18n.t('bookList.' + taskData.source_sentence?.book_title)} ${taskData.source_sentence?.chapter_title} : ${taskData.source_sentence?.sentence_number}`,
        taskTypeId: task.task_type_id,
        taskDataId: task.task_data_id,
        projectId: task.project_id,
        assignmentId: task.assignment_id,
        isBookmarked: task.is_bookmarked,
        isCompleted: task.status_id === 4,
        sourceText: taskData.source_sentence?.sentence_text || '',
        targetText: taskData.translated_sentence?.sentence_text,
        targetTextId: taskData.translated_sentence?.id,
        sentenceText: taskData.translated_sentence?.sentence_text,
        notesList: getNotes(task.notes),
        bookId: taskData.source_sentence?.book_id,
      } as WorkspaceAssignmentTask;
    });
  },
);

export const bookmarkPeerReviewTask = createAppAsyncThunk(
  'assignments/bookmarkPeerReviewTask',
  async ({ taskId, isBookmarked }: { taskId: number, isBookmarked: boolean }) => {
    const result = await graphqlMutation<BookmarkPeerReviewTask, { bookmarkTask: BookmarkTask }>(
      BOOKMARK_TASK_MUTATION,
      { taskId, isBookmarked },
    );

    return result.bookmarkTask;
  },
);

export const updateTaskPeerReview = createAppAsyncThunk(
  'assignments/updateTaskPeerReview',
  async ({ taskId, translated_sentence_id, sentence_text, selectedTaskId }: { taskId: number, translated_sentence_id: number, sentence_text: string, selectedTaskId: number }) => {
    const data = await graphqlMutation<UpdateTaskPeerReviewMutationVariables, UpdateTaskPeerReviewMutation>(
      UPDATE_TASK_PEER_REVIEW,
      { task_id: taskId, translated_sentence_id, sentence_text },
    );
    return { taskId, selectedTaskId, ...data.updateTaskPeerReview };
  },
);

export const upsertNote = createAppAsyncThunk(
  'peerReview/upsertNote',
  async ({ taskId, userId, noteText, noteId }: UpsertNote) => {
    const data = await graphqlMutation<UpsertTaskNoteMutationVariables, UpsertTaskNoteMutation>(
      UPSERT_TASK_NOTE,
      { taskId, userId, noteText, noteId },
    );

    return {
      data,
      taskId,
    };
  },
);

const incompleteTaskList = (taskList: WorkspaceAssignmentTask[]) => {
  return taskList
    .filter(obj => obj.isCompleted === false);
};
const findNextIncompleteTask = (
  taskList: WorkspaceAssignmentTask[],
  selectedTaskId: number,
): number | undefined => {
  const incompleteTasks = incompleteTaskList(taskList);

  const selectedIndex = incompleteTasks.findIndex(
    (obj: WorkspaceAssignmentTask) => obj.id === selectedTaskId,
  );

  // check immediate next incomplete task
  if (selectedIndex >= 0 && incompleteTasks[selectedIndex + 1]) {
    return incompleteTasks[selectedIndex + 1].id;
  }
};

export const peerReviewReducer = createSlice({
  name: 'peerReview',
  initialState,
  reducers: {

    setSelectedTaskId: (state, action: PayloadAction<number | null>) => {
      state.selectedTaskId = action.payload;
    },
    selectNextTaskId: (
      state,
      action: PayloadAction<{ currentSelectedTaskId: number, showCompletedTasks: boolean }>,
    ) => {
      const { currentSelectedTaskId, showCompletedTasks } = action.payload;

      if (!showCompletedTasks) {
        const nextIncompleteId = findNextIncompleteTask(state.allTaskList, currentSelectedTaskId);
        state.selectedTaskId = nextIncompleteId ?? null;
      } else {
        const currentIndex = state.allTaskList.findIndex(task => task.id === currentSelectedTaskId);
        const nextTask = state.allTaskList[currentIndex + 1];
        state.selectedTaskId = nextTask?.id ?? null;
      }
    },
    handleBookmark: (state, action: PayloadAction<number>) => {
      const currentTaskIndex = state.allTaskList.findIndex(
        (obj: WorkspaceAssignmentTask) => obj.id === action.payload,
      );
      if (currentTaskIndex >= 0) {
        state.allTaskList[currentTaskIndex].isBookmarked = !state.allTaskList[currentTaskIndex].isBookmarked;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPeerReviewTaskList.pending, (state) => {
      state.status = STATUSES.PENDING;
    });
    builder.addCase(fetchPeerReviewTaskList.fulfilled, (state, action) => {
      state.allTaskList = action.payload;
      state.selectedTaskId = action.payload.find(task => !task.isCompleted)?.id ?? action.payload[0]?.id ?? null;
      state.status = STATUSES.FULFILLED;
    });
    builder.addCase(fetchPeerReviewTaskList.rejected, (state) => {
      state.status = STATUSES.REJECTED;
      state.allTaskList = initialState.allTaskList;
      state.selectedTaskId = initialState.selectedTaskId;
    });
    builder.addCase(bookmarkPeerReviewTask.fulfilled, (state, action) => {
      if (action.payload) {
        const { id, is_bookmarked } = action.payload;
        const taskIndex = state.allTaskList.findIndex(task => task.id === id);
        if (taskIndex >= 0) {
          state.allTaskList[taskIndex].isBookmarked = Boolean(is_bookmarked);
        }
      }
    });
    builder.addCase(bookmarkPeerReviewTask.rejected, () => {
      showToast(errorMessages.bookmarkingTask, 'error');
    });
    builder.addCase(updateTaskPeerReview.fulfilled, (state, action) => {
      const { taskId } = action.payload;
      const taskIndex = state.allTaskList.findIndex(existingTask => existingTask.id === taskId);
      const sentenceText = action.meta.arg.sentence_text;
      if (taskIndex >= 0) {
        state.allTaskList[taskIndex] = {
          ...state.allTaskList[taskIndex],
          sentenceText,
          targetText: sentenceText,
          isCompleted: true,
        };
      }
    });
    builder.addCase(updateTaskPeerReview.rejected, () => {
      showToast(errorMessages.updatingTask, 'error');
    });
    builder.addCase(upsertNote.fulfilled, (state, action) => {
      const { taskId, data } = action.payload ?? {};
      const note = data?.upsertTaskNote;
      if (!note) return;
      const taskIndex = state.allTaskList.findIndex(task => task.id === taskId);
      const updatedTasks = [...state.allTaskList];
      const updatedNotes = [...updatedTasks[taskIndex].notesList];

      const noteIndex = updatedNotes.findIndex(taskNote => taskNote.id === note.id);
      if (noteIndex < 0) {
        updatedNotes.push(note);
      } else {
        updatedNotes[noteIndex] = note;
      }

      updatedTasks[taskIndex] = {
        ...updatedTasks[taskIndex],
        notesList: updatedNotes,
      };

      state.allTaskList = updatedTasks;
    });

    builder.addCase(upsertNote.rejected, () => {
      showToast(errorMessages.savingNote, 'error');
    });
  },
});

export const allTaskList = (state: RootState) => state.peerReviewReducer.allTaskList;

export const selectMappedTaskList = createSelector(
  [allTaskList],
  tasks =>
    tasks.map(({ id, sourceText, title, isCompleted, isBookmarked }) => ({
      id,
      content: sourceText,
      title,
      isCompleted,
      isBookmarked,
    })),
);

export const getCompletedTaskList = createSelector([selectMappedTaskList], (tasks) => {
  return tasks.filter(({ isCompleted }) => isCompleted === true);
});

export const getIncompletedTaskList = createSelector([selectMappedTaskList], (tasks) => {
  return tasks.filter(({ isCompleted }) => isCompleted === false);
});

export const getSelectedTaskById = createSelector(
  [allTaskList, (_, selectedTaskId: number | null) => selectedTaskId],
  (tasks, selectedTaskId) => {
    return tasks.find((obj: WorkspaceAssignmentTask) =>
      obj.id === selectedTaskId,
    );
  },
);

export const selectPeerReviewReducerStatus = (state: RootState) => state.peerReviewReducer?.status;

export const {
  setSelectedTaskId,
  selectNextTaskId,
} = peerReviewReducer.actions;
export default peerReviewReducer.reducer;
